import { useState, useEffect, useCallback, useMemo } from 'react';
import { hasuraClient } from '../../../utils/graphqlClient';
import { GET_ACTIVE_REPORTS_BY_DATE } from '../../../graphql/queries/reportManagement';
import { useAuth } from '../../../hooks';
import { matchFlowScope } from '../../../utils/flowScopeMatcher';

/**
 * Fetches all available reports matching a flow's criteria,
 * filters out reports already in the flow, and returns
 * virtual backlog items compatible with KanbanCard.
 */
const useBacklogReports = (flow, existingReportIds) => {
  const { user } = useAuth();
  const isRoot = user?.role === 'ROOT';

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchJobs = useCallback(async () => {
    if (!flow) return;
    setLoading(true);
    try {
      const conditions = [];

      if (!isRoot && user?.companyId) {
        conditions.push({ company_id: { _eq: user.companyId } });
      }

      if (Array.isArray(flow.job_types) && flow.job_types.length > 0) {
        conditions.push({ job_type: { _in: flow.job_types } });
      }

      const where = conditions.length > 0 ? { _and: conditions } : {};
      const variables = Object.keys(where).length > 0 ? { where } : {};
      const result = await hasuraClient.query(GET_ACTIVE_REPORTS_BY_DATE, variables);
      let fetchedJobs = result.jobs || [];

      // Only jobs with reports
      fetchedJobs = fetchedJobs.filter(
        (j) => Array.isArray(j.job_reports) && j.job_reports.length > 0
      );

      // Apply the SAME scope rules used by populateFlowItems / auto-add
      // (matchFlowScope) so the backlog and the flow itself agree on what
      // belongs in scope. Previously this hook used the legacy
      // filter_config schema while auto-add used the new start_date /
      // end_date / report_type_ids fields, so backlog could show reports
      // that auto-add had already inserted as flow items.
      fetchedJobs = fetchedJobs
        .map((j) => ({
          ...j,
          job_reports: (j.job_reports || []).filter((r) =>
            matchFlowScope(flow, j, r),
          ),
        }))
        .filter((j) => j.job_reports.length > 0);

      setJobs(fetchedJobs);
    } catch (err) {
      console.error('Backlog reports fetch error:', err);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [flow, isRoot, user?.companyId]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Transform jobs into virtual backlog items, excluding already-added ones
  const backlogItems = useMemo(() => {
    const items = [];
    for (const job of jobs) {
      for (const report of job.job_reports || []) {
        if (existingReportIds.has(String(report.id))) continue;
        items.push({
          id: `backlog-${job.id}-${report.id}`,
          job_id: job.id,
          report_id: String(report.id),
          job: job,
          column_id: null,
          is_archived: false,
          completion_date: null,
          submission_date: null,
        });
      }
    }
    return items;
  }, [jobs, existingReportIds]);

  return { backlogItems, loading, refetch: fetchJobs };
};

export default useBacklogReports;
